import React, { useState, useRef, useEffect } from 'react';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/icons-material/RotateRight';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import SettingsIcon from '@mui/icons-material/Settings';
import AssistantIcon from '@mui/icons-material/Assistant';
import ArticleIcon from '@mui/icons-material/Article';
import SubjectIcon from '@mui/icons-material/Subject';
import SchoolIcon from '@mui/icons-material/School';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import Tooltip from '@mui/material/Tooltip';
import { getRightworkAPI, postRightworkAPI } from '../requests/utilities/requests';
import { streamRightworkAPI } from '../requests/utilities/streamingRequests';
import {
  API_get_file_system_insights,
  API_get_file_system_insights_stream,
  API_update_assistant_interaction_feedback,
} from '../../../constants';
import ReactMarkdown from 'react-markdown'
import VoiceAssistantDisplay from './VoiceAssistantDisplay'

export default function AssistantDisplay(props) {
  const [messages, setMessages] = useState([
    {
      id: 1,
      content: "Hi there! I'm your AI assistant. Ask me anything about recipes, SOPs, or procedures, and I'll help you find what you need.",
      sender: "ai",
      timestamp: new Date(),
      sources: []
    },
  ]);
  const [expandedSources, setExpandedSources] = useState({});
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeechEnabled, setIsSpeechEnabled] = useState(false);
  const [showVoiceMode, setShowVoiceMode] = useState(false);
  const [previousResponseId, setPreviousResponseId] = useState(null);
  const [voiceSessionKey, setVoiceSessionKey] = useState(0);

  useEffect(() => {
    if (!props.company?.voice_assistant_enabled) {
      setShowVoiceMode(false);
    }
  }, [props.company?.voice_assistant_enabled]);
  const streamingDisabledForCompany = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Function to format timestamp
  const formatTimestamp = (timestamp) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(timestamp);
  };

  // Function to toggle source expansion
  const toggleSources = (messageId) => {
    setExpandedSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Build recent conversation history for pgvector multi-turn context.
  // Excludes the initial greeting and the just-sent user message (which is
  // passed separately as user_query).  Limited to the last 6 messages
  // (3 exchanges).
  const getConversationHistory = () => {
    const history = messages
      .filter(m => m.sender === "user" || m.sender === "ai")
      .slice(1) // skip initial greeting
      .slice(-6)
      .map(m => ({
        role: m.sender === "user" ? "user" : "assistant",
        content: m.content,
      }));
    return history.length > 0 ? history : undefined;
  };

  // Function to fetch response from API
  const fetchAIResponse = async (query) => {
    try {
      const conversationHistory = getConversationHistory();
      const apiParams = {
        company_id: props.homeCompany,
        store_id: props.homeStore,
        user_query: query,
        ...(previousResponseId && { previous_response_id: previousResponseId }),
        ...(conversationHistory && { conversation_history: conversationHistory }),
      };

      const response = await postRightworkAPI(API_get_file_system_insights, apiParams);

      console.log("RESPONSE", response);

      if (response.status === 200) {
        const responsePayload = response.data?.redux?.payload?.response || {};
        const aiResponse = responsePayload.text || "Sorry, I couldn't find an answer for that.";
        const annotations = responsePayload.annotations || [];

        console.log("ANNOTATIONS", annotations)

        // Process annotations to match the expected sources format
        const formattedSources = annotations.map(annotation => ({
          id: annotation.uuid || Math.random().toString(),
          title: annotation.name || "Unknown document",
          path: (annotation.type === "FileUpload" ? annotation.presigned_url : annotation.pdf) || "/",
          extension: annotation.extension || "",
          fileSize: annotation.fileSize || "",
          type: annotation.type || "file"
        }));

        if (response.data?.redux?.payload?.response?.id) {
          setPreviousResponseId(response.data?.redux?.payload?.response?.id);
        }

        return {
          text: aiResponse,
          sources: formattedSources,
          interactionLogUuid: responsePayload.interaction_log_uuid || null,
          interactionKey: responsePayload.interaction_key || null,
        };
      } else {
        console.error("API error:", response);
        return {
          text: "Sorry, I encountered an error while processing your request.",
          sources: [],
          interactionLogUuid: null,
          interactionKey: null,
        };
      }
    } catch (error) {
      console.error("Error fetching AI response:", error);
      return {
        text: "Sorry, there was a problem connecting to the assistant service.",
        sources: [],
        interactionLogUuid: null,
        interactionKey: null,
      };
    }
  };

  // Function to fetch response via SSE streaming, with non-streaming fallback
  const fetchAIResponseStreaming = async (query) => {
    const aiMessageId = Date.now() + 1;

    const conversationHistory = getConversationHistory();
    const apiParams = {
      company_id: props.homeCompany,
      store_id: props.homeStore,
      user_query: query,
      ...(conversationHistory && { conversation_history: conversationHistory }),
    };

    let receivedTokens = false;

    try {
      await streamRightworkAPI(
        API_get_file_system_insights_stream,
        apiParams,
        // onToken
        (tokenText) => {
          if (!receivedTokens) {
            receivedTokens = true;
            // Add placeholder AI message on first token, keep isTyping
            // true to block input until the stream finishes
            setMessages(prev => [...prev, {
              id: aiMessageId,
              content: tokenText,
              sender: "ai",
              timestamp: new Date(),
              sources: [],
              interactionLogUuid: null,
              interactionKey: null,
              feedbackRating: null,
              feedbackSaving: false,
              supportFollowUpStatus: null,
            }]);
          } else {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === aiMessageId
                  ? { ...msg, content: msg.content + tokenText }
                  : msg
              )
            );
          }
        },
        // onDone
        (donePayload) => {
          const annotations = donePayload.annotations || [];
          const formattedSources = annotations.map(annotation => ({
            id: annotation.uuid || Math.random().toString(),
            title: annotation.name || "Unknown document",
            path: (annotation.type === "FileUpload" ? annotation.presigned_url : annotation.pdf) || "/",
            extension: annotation.extension || "",
            fileSize: annotation.fileSize || "",
            type: annotation.type || "file"
          }));

          if (!receivedTokens) {
            // No tokens arrived — append a new message with the done text
            setMessages(prev => [...prev, {
              id: aiMessageId,
              content: donePayload.text || "I couldn't generate a response. Please try again.",
              sender: "ai",
              timestamp: new Date(),
              sources: formattedSources,
              interactionLogUuid: donePayload.interaction_log_uuid || null,
              interactionKey: donePayload.interaction_key || null,
              feedbackRating: null,
              feedbackSaving: false,
              supportFollowUpStatus: null,
            }]);
          } else {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === aiMessageId
                  ? {
                      ...msg,
                      content: donePayload.text || msg.content,
                      sources: formattedSources,
                      interactionLogUuid: donePayload.interaction_log_uuid || null,
                      interactionKey: donePayload.interaction_key || null,
                      feedbackRating: null,
                      feedbackSaving: false,
                      supportFollowUpStatus: null,
                    }
                  : msg
              )
            );
          }
          setIsTyping(false);
        },
        // onError — throw so the catch block triggers the fallback
        (errorMsg) => {
          throw new Error(errorMsg);
        }
      );
    } catch (streamError) {
      // Only permanently disable streaming if the server explicitly rejected
      // the request (400 = feature not enabled). Transient errors (5xx,
      // network drops, etc.) should retry streaming on the next request.
      if (streamError.status === 400) {
        streamingDisabledForCompany.current = props.homeCompany;
      }

      // Remove the placeholder message if streaming added one
      if (receivedTokens) {
        setMessages(prev => prev.filter(msg => msg.id !== aiMessageId));
      }

      // Fallback to non-streaming
      const aiResponseData = await fetchAIResponse(query);
      setMessages(prev => [...prev, {
        id: aiMessageId,
        content: aiResponseData.text,
        sender: "ai",
        timestamp: new Date(),
        sources: aiResponseData.sources || [],
        interactionLogUuid: aiResponseData.interactionLogUuid || null,
        interactionKey: aiResponseData.interactionKey || null,
        feedbackRating: null,
        feedbackSaving: false,
        supportFollowUpStatus: null,
      }]);
      setIsTyping(false);
    }
  };

  // Function to handle sending messages
  const handleSendMessage = async () => {
    const text = inputValue;

    if (text.trim() === '') return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      content: text.trim(),
      sender: "user",
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue(""); // Clear input

    // Show typing indicator
    setIsTyping(true);

    try {
      if (streamingDisabledForCompany.current === props.homeCompany) {
        const aiResponseData = await fetchAIResponse(text.trim());
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          content: aiResponseData.text,
          sender: "ai",
          timestamp: new Date(),
          sources: aiResponseData.sources || [],
          interactionLogUuid: aiResponseData.interactionLogUuid || null,
          interactionKey: aiResponseData.interactionKey || null,
          feedbackRating: null,
          feedbackSaving: false,
          supportFollowUpStatus: null,
        }]);
        setIsTyping(false);
      } else {
        await fetchAIResponseStreaming(text.trim());
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        content: "Sorry, I couldn't process your request at the moment. Please try again later.",
        sender: "system",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsTyping(false);
    }
  };

  const handleAssistantFeedback = async (messageId, rating) => {
    const target = messages.find((message) => message.id === messageId);
    if (!target || (!target.interactionLogUuid && !target.interactionKey)) return;

    const previousRating = target.feedbackRating;
    const nextRating = previousRating === rating ? null : rating;
    setMessages(prev =>
      prev.map(message =>
        message.id === messageId
          ? {
              ...message,
              feedbackRating: nextRating,
              feedbackSaving: true,
              supportFollowUpStatus:
                nextRating === "thumbs_down"
                  ? (message.supportFollowUpStatus || "suggested")
                  : (!nextRating && message.supportFollowUpStatus === "suggested")
                  ? "not_requested"
                  : (message.supportFollowUpStatus === "suggested" ? "not_requested" : message.supportFollowUpStatus),
            }
          : message
      )
    );

    try {
      const res = await postRightworkAPI(API_update_assistant_interaction_feedback, {
        interaction_uuid: target.interactionLogUuid,
        interaction_key: target.interactionKey,
        feedback_rating: nextRating || "clear",
      });
      const interaction = res?.data?.redux?.payload?.interaction || {};
      setMessages(prev =>
        prev.map(message =>
          message.id === messageId
            ? {
                ...message,
                feedbackSaving: false,
                feedbackRating: interaction.feedback_rating || null,
                supportFollowUpStatus: interaction.support_follow_up_status || message.supportFollowUpStatus,
              }
            : message
        )
      );
    } catch (error) {
      setMessages(prev =>
        prev.map(message =>
          message.id === messageId
            ? {
                ...message,
                feedbackSaving: false,
                feedbackRating: previousRating || null,
              }
            : message
        )
      );
    }
  };

  const handleCopyAssistantMessage = async (messageId, content) => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      window.setTimeout(() => {
        setCopiedMessageId(current => (current === messageId ? null : current));
      }, 1200);
    } catch (error) {
      setCopiedMessageId(null);
    }
  };

  const handleAssistantSupportRequest = async (messageId) => {
    const target = messages.find((message) => message.id === messageId);
    if (!target || (!target.interactionLogUuid && !target.interactionKey)) return;

    const previousStatus = target.supportFollowUpStatus;
    setMessages(prev =>
      prev.map(message =>
        message.id === messageId
          ? {
              ...message,
              feedbackRating: "thumbs_down",
              supportFollowUpStatus: "requested",
              supportFollowUpSaving: true,
            }
          : message
      )
    );

    try {
      const res = await postRightworkAPI(API_update_assistant_interaction_feedback, {
        interaction_uuid: target.interactionLogUuid,
        interaction_key: target.interactionKey,
        feedback_rating: "thumbs_down",
        support_follow_up_requested: true,
      });
      const interaction = res?.data?.redux?.payload?.interaction || {};
      setMessages(prev =>
        prev.map(message =>
          message.id === messageId
            ? {
                ...message,
                feedbackRating: interaction.feedback_rating || "thumbs_down",
                supportFollowUpStatus: interaction.support_follow_up_status || "requested",
                supportFollowUpSaving: false,
              }
            : message
        )
      );
    } catch (error) {
      setMessages(prev =>
        prev.map(message =>
          message.id === messageId
            ? {
                ...message,
                supportFollowUpStatus: previousStatus || null,
                supportFollowUpSaving: false,
              }
            : message
        )
      );
    }
  };

  // Function to handle key press events
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  // Toggle listening state - just UI, no actual functionality
  const toggleListening = () => {
    setIsListening(!isListening);

    // If turning off "listening", send whatever is in the input field
    if (isListening && inputValue.trim() !== '') {
      handleSendMessage();
    }
  };

  // Toggle speech state - just UI, no actual functionality
  const toggleSpeech = () => {
    setIsSpeechEnabled(!isSpeechEnabled);
  };

  // Toggle between voice and text mode interfaces
  const toggleVoiceMode = () => {
    setShowVoiceMode(!showVoiceMode);
  };

  // Reset conversation and clear previous response IDs
  const resetConversation = () => {
    if (showVoiceMode) {
      // Remount VoiceAssistantDisplay to disconnect and start fresh
      setVoiceSessionKey((k) => k + 1);
    }
    setMessages([
      {
        id: Date.now(),
        content: "Hi there! I'm your AI assistant. Ask me anything about recipes, SOPs, or procedures, and I'll help you find what you need.",
        sender: "ai",
        timestamp: new Date(),
        sources: []
      },
    ]);
    setPreviousResponseId(null);
    setExpandedSources({});
  };

  // Function to format file size
  const formatFileSize = (size) => {
    if (!size) return "";
    return size;
  };

   // Custom components for ReactMarkdown
   const components = {
    h1: ({ node, ordered, ...props }) => <h1 className="text-3xl font-bold mt-8" {...props} />,
    h2: ({ node, ordered, ...props }) => <h2 className="text-2xl font-bold mt-7" {...props} />,
    h3: ({ node, ordered, ...props }) => <h3 className="text-xl font-bold mt-6" {...props} />,
    h4: ({ node, ordered, ...props }) => <h4 className="text-lg font-bold mt-5" {...props} />,
    h5: ({ node, ordered, ...props }) => <h5 className="text-base font-semibold mt-4" {...props} />,
    h6: ({ node, ordered, ...props }) => <h6 className="text-sm font-semibold mt-3" {...props} />,
    p: ({ node, ordered, ...props }) => <p className="mt-3 text-base" {...props} />,
    strong: ({ node, ordered, ...props }) => <strong className="font-semibold my-3 text-sm" {...props} />,
    em: ({ node, ordered, ...props }) => <em className="italic mt-3 text-sm" {...props} />,
    ul: ({ node, ordered, ...props }) => <ul className="list-disc ml-6 mt-3 text-sm" {...props} />,
    ol: ({ node, ordered, ...props }) => <ol className="list-decimal ml-6 mt-3 text-sm" {...props} />,
    li: ({ node, ordered, ...props }) => <li className="mt-3 text-sm" {...props} />,
    blockquote: ({ node, ordered, ...props }) => <blockquote className="border-l-4 border-slate-300 pl-4 italic mt-3 text-sm" {...props} />,
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* Main chat interface */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center min-w-0">
            {props.onBackToKnowledgeBase && (
              <>
                <button
                  onClick={props.onBackToKnowledgeBase}
                  title="Back to Company Files"
                  className="flex items-center gap-1.5 -ml-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-violet-700 hover:bg-gray-100 transition-colors"
                >
                  <ArrowBackIcon style={{ fontSize: 18 }} />
                  <span>Company Files</span>
                </button>
                <div className="h-6 w-px bg-gray-200 mx-3 flex-shrink-0" />
              </>
            )}
            <div className="w-9 h-9 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0">
              <AssistantIcon fontSize="small" className="text-white" />
            </div>
            <div className="ml-3 min-w-0">
              <h2 className="font-medium text-gray-800">Chat with Company Files</h2>
              <p className="text-xs text-gray-500 truncate">Searches files in AI-enabled folders</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Reset conversation button */}
            <button
              onClick={resetConversation}
              className="text-sm text-gray-500 hover:text-violet-600"
            >
              Reset
            </button>

            {/* Voice Mode Toggle Button */}
            {props.company?.voice_assistant_enabled && (
              <button
                onClick={toggleVoiceMode}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors"
              >
                {showVoiceMode ? (
                  <>
                    <ChatIcon fontSize="small" />
                    <span className="text-sm">Text Mode</span>
                  </>
                ) : (
                  <>
                    <MicIcon fontSize="small" />
                    <span className="text-sm">Voice Mode</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {showVoiceMode ? (
          <VoiceAssistantDisplay
            key={voiceSessionKey}
            companyId={props.homeCompany}
            storeId={props.homeStore}
            onClose={toggleVoiceMode}
          />
        ) : (
          <>
            {/* Messages area */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
              {messages.length === 1 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                  <div className="w-16 h-16 rounded-2xl bg-violet-600 flex items-center justify-center mb-4 shadow-sm">
                    <AssistantIcon style={{ fontSize: 32 }} className="text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">How can I help?</h3>
                  <p className="text-sm text-gray-500 mt-1.5 max-w-md">
                    Ask anything about recipes, SOPs, or procedures — I'll search your AI-enabled folders to find the answer.
                  </p>
                </div>
              ) : (
                <div>
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`mb-4 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.sender === 'system' ? (
                    <div className="bg-gray-200 rounded-lg px-4 py-2 max-w-3/4 text-sm text-gray-700">
                      {message.content}
                    </div>
                  ) : (
                    <div
                      className={`rounded-lg px-4 max-w-3/4 ${
                        message.sender === 'user'
                          ? 'bg-violet-100 text-gray-700 py-3'
                          : 'bg-white border border-gray-200 text-gray-700 pb-3'
                      }`}
                    >
                      <div className="flex flex-col">
                        {(message.sender === 'system' || message.sender === 'ai') ? <ReactMarkdown components={components} className="mb-1">{message.content}</ReactMarkdown> : <div className="mb-1">{message.content}</div>}

                        {/* Sources UI - Only show for AI messages with sources */}
                        {message.sender === 'ai' && message.sources && message.sources.length > 0 && (
                          <div className="mt-3 border-t border-gray-100 pt-3">
                            <button
                              onClick={() => toggleSources(message.id)}
                              className="flex items-center text-xs text-violet-600 hover:text-violet-800 transition-colors mb-1"
                            >
                              <ArticleIcon fontSize="small" className="mr-1" style={{ fontSize: '14px' }} />
                              <span>{message.sources.length} source{message.sources.length > 1 ? 's' : ''}</span>
                              {expandedSources[message.id] ? (
                                <ExpandLessIcon fontSize="small" className="ml-1" style={{ fontSize: '14px' }} />
                              ) : (
                                <ExpandMoreIcon fontSize="small" className="ml-1" style={{ fontSize: '14px' }} />
                              )}
                            </button>

                            {expandedSources[message.id] && (
                              <div className="mt-2 bg-gray-50 rounded p-3 text-xs">
                                {message.sources.map((source, index) => (
                                  <div key={source.id} className={`${index > 0 ? 'mt-2 pt-3 pb-2 border-t border-gray-100' : ''}`}>
                                    <a
                                      href={source.path}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex space-x-2 hover:bg-gray-100 p-1 rounded transition-colors"
                                    >
                                      <div
                                        className="rounded w-5 h-6 flex items-center justify-center border border-gray-200 bg-white"
                                      >
                                        {source.type === "LearningSection" ? <LocalLibraryIcon style={{ fontSize: "14px" }} className="text-gray-500" /> : <SubjectIcon style={{ fontSize: "16px" }} className="text-gray-500" />}

                                      </div>
                                      <div className="flex-1">
                                        <div className="font-medium text-gray-700">{source.title}</div>
                                        <div className="text-gray-500 truncate mt-1.5 flex items-center">
                                          {source.fileSize && (
                                            <span className="text-gray-400">{formatFileSize(source.fileSize)}</span>
                                          )}
                                        </div>
                                      </div>
                                    </a>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {message.sender === 'ai' && (message.interactionLogUuid || message.interactionKey) && (
                          <div className="mt-3 border-t border-gray-100 pt-2">
                            <div className="flex items-center gap-0.5">
                              <Tooltip title={copiedMessageId === message.id ? "Copied" : "Copy"} arrow>
                                <button
                                  type="button"
                                  aria-label="Copy response"
                                  onClick={() => handleCopyAssistantMessage(message.id, message.content)}
                                  className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  <ContentCopyIcon style={{ fontSize: "17px" }} />
                                </button>
                              </Tooltip>
                              <Tooltip title="Good response" arrow>
                                <span>
                                  <button
                                    type="button"
                                    aria-label="Good response"
                                    disabled={message.feedbackSaving}
                                    onClick={() => handleAssistantFeedback(message.id, "thumbs_up")}
                                    className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                                      message.feedbackRating === "thumbs_up"
                                        ? "bg-gray-100 text-gray-800"
                                        : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                                    } ${message.feedbackSaving ? "opacity-60" : ""}`}
                                  >
                                    {message.feedbackRating === "thumbs_up" ? (
                                      <ThumbUpIcon style={{ fontSize: "17px" }} />
                                    ) : (
                                      <ThumbUpOffAltIcon style={{ fontSize: "17px" }} />
                                    )}
                                  </button>
                                </span>
                              </Tooltip>
                              <Tooltip title="Bad response" arrow>
                                <span>
                                  <button
                                    type="button"
                                    aria-label="Bad response"
                                    disabled={message.feedbackSaving}
                                    onClick={() => handleAssistantFeedback(message.id, "thumbs_down")}
                                    className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                                      message.feedbackRating === "thumbs_down"
                                        ? "bg-gray-100 text-gray-800"
                                        : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                                    } ${message.feedbackSaving ? "opacity-60" : ""}`}
                                  >
                                    {message.feedbackRating === "thumbs_down" ? (
                                      <ThumbDownIcon style={{ fontSize: "17px" }} />
                                    ) : (
                                      <ThumbDownOffAltIcon style={{ fontSize: "17px" }} />
                                    )}
                                  </button>
                                </span>
                              </Tooltip>
                            </div>
                            {message.feedbackRating === "thumbs_down" && (
                              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                                {message.supportFollowUpSaving ? (
                                  <span className="text-gray-500">Notifying Rightwork support...</span>
                                ) : message.supportFollowUpStatus === "sent" ? (
                                  <span className="text-gray-500">Rightwork support notified</span>
                                ) : (
                                  <>
                                    <span>
                                      {message.supportFollowUpStatus === "failed"
                                        ? "Could not send."
                                        : "Want Rightwork to review this?"}
                                    </span>
                                    <button
                                      type="button"
                                      disabled={message.supportFollowUpSaving}
                                      onClick={() => handleAssistantSupportRequest(message.id)}
                                      className="font-medium text-violet-700 hover:text-violet-800 disabled:opacity-60"
                                    >
                                      {message.supportFollowUpStatus === "failed" ? "Try again" : "Notify support"}
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        <span className={`text-xs self-end mt-1 ${message.sender === 'user' ? 'text-gray-500' : 'text-gray-400'}`}>
                          {formatTimestamp(message.timestamp)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="flex items-center mb-4">
                  <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 max-w-3/4 flex items-center">
                    <CircularProgress fontSize="small" className="text-violet-600 animate-spin mr-2" />
                    <span className="text-gray-500 text-sm">Assistant is thinking...</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Text Mode Input */}
            <div className="bg-white border-t p-4">
              <div className="flex">
                <div className="flex-1 bg-gray-100 rounded-full flex items-center px-4">
                  <textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Ask about recipes, procedures, or kitchen SOPs..."
                    className="w-full bg-transparent py-2 outline-none resize-none text-gray-700"
                    rows="1"
                  />
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputValue.trim() || isTyping}
                    className={`ml-2 rounded-full w-8 h-8 flex items-center justify-center ${
                      inputValue.trim() && !isTyping ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <SendIcon fontSize="small" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
